import {
  collection,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  setDoc,
  updateDoc,
  writeBatch,
  arrayRemove,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import * as Sentry from "@sentry/react-native";
import db from "./FirestoreConfig";
import { Client } from "./Client";
import { runInAction } from "mobx";
import {
  INVITATION_ACTIONABLE_STATES,
  INVITATION_STATE,
  normalizeInvitationState,
  resolveInvitationEmail,
} from "../invitations/invitationIdentity";
import { shouldUsePendingBlockPath } from "../invitations/clientBlockPathResolver";
import {
  buildInvitationMappingAuditPlan,
  normalizeMappingEmail,
  selectCanonicalPendingClientForEmail,
  shouldSkipPendingClientCreation,
} from "../invitations/invitationMappingResolver";
import {
  isInvitationMappingHardeningEnabled,
  refreshInvitationMappingHardeningFlag,
} from "../invitations/webInvitationRolloutFlags";
import { resolvePendingInvitationBlockHeadline } from "../invitations/pendingInvitationBlockHeadline";
import { normalizePendingWorkspaceWeekWorkouts } from "../invitations/pendingWorkspaceState";
import {
  remapClientPendingBlockIdsToPromotedIds,
  shouldRetainAcceptedPendingClientForDeferredSync,
} from "../invitations/pendingAcceptanceSyncReconciliation";

class CoachFirestoreListener {
  clientsListener = null;
  invitationsListener = null;

  chatsListener = null;
  mappingTouchedEmails = new Set();
  mappingReconciliationInFlight = false;
  mappingAuditUpdatesInFlight = new Set();
  mappingConflictEmails = new Set();
  pendingInvitationHeadlineInFlight = new Map();
  pendingInvitationHeadlineHydratedPendingUsers = new Map();
  pendingInvitationHeadlineRequestSeq = 0;

  constructor(coach) {
    this.coach = coach;
    this.reconcilePendingClientRoutingForSync =
      this.reconcilePendingClientRoutingForSync.bind(this);
  }

  resolveInvitationCreatedAtMs(value) {
    if (!value) return null;

    if (typeof value?.toMillis === "function") {
      const millis = value.toMillis();
      return Number.isFinite(millis) ? millis : null;
    }

    if (value instanceof Date) {
      const millis = value.getTime();
      return Number.isFinite(millis) ? millis : null;
    }

    const seconds = Number(value?.seconds);
    if (Number.isFinite(seconds)) {
      const nanos = Number(value?.nanoseconds);
      const millisFromNanos = Number.isFinite(nanos)
        ? Math.floor(nanos / 1e6)
        : 0;
      return seconds * 1000 + millisFromNanos;
    }

    return null;
  }

  normalizeInvitationPromotionStatus(value) {
    return typeof value === "string" ? value.trim().toUpperCase() : "";
  }

  normalizeInvitationMappingStatus(value) {
    return typeof value === "string" ? value.trim().toUpperCase() : "";
  }

  normalizeMappingResolutionReason(value) {
    return typeof value === "string" ? value.trim().toUpperCase() : "";
  }

  normalizeSummaryString(value) {
    return typeof value === "string" ? value.trim() : "";
  }

  getActiveUserIdsForEmail(coach, normalizedEmail) {
    if (!coach || !normalizedEmail) return [];

    const dedupedUserIds = [];
    const seenIds = new Set();

    (coach.activeClients || []).forEach((client) => {
      const clientEmail = normalizeMappingEmail(client?.email);
      if (clientEmail !== normalizedEmail) return;

      const userId = this.normalizeSummaryString(client?.id);
      if (!userId) return;

      const normalizedUserId = userId.toLowerCase();
      if (seenIds.has(normalizedUserId)) return;
      seenIds.add(normalizedUserId);
      dedupedUserIds.push(userId);
    });

    return dedupedUserIds;
  }

  recordMappingTouchedEmail(normalizedEmail) {
    const value = this.normalizeSummaryString(normalizedEmail).toLowerCase();
    if (!value) return;
    this.mappingTouchedEmails.add(value);
  }

  isInvitationMappingHardeningEnabled() {
    return isInvitationMappingHardeningEnabled() === true;
  }

  hasUnresolvedActiveUserConflictForEmail(coach, normalizedEmail) {
    if (!normalizedEmail) return false;

    if (this.mappingConflictEmails.has(normalizedEmail)) {
      return true;
    }

    return this.getActiveUserIdsForEmail(coach, normalizedEmail).length > 1;
  }

  shouldSkipPendingPromotionForConflict({ coach, normalizedEmail }) {
    if (!this.isInvitationMappingHardeningEnabled()) {
      return false;
    }

    const hasConflict = this.hasUnresolvedActiveUserConflictForEmail(
      coach,
      normalizedEmail
    );
    if (hasConflict) {
      console.warn("[invitation-mapping-hardening] promotion_skipped_conflict", {
        coachId: coach?.id ?? null,
        normalizedEmail,
      });
    }

    return hasConflict;
  }

  shouldApplyInvitationMappingPatch(summary, patch) {
    if (!summary || !patch) {
      return true;
    }

    const currentStatus = this.normalizeInvitationMappingStatus(
      summary?.mappingStatus
    );
    const nextStatus = this.normalizeInvitationMappingStatus(
      patch?.mapping_status
    );
    if (currentStatus !== nextStatus) return true;

    const currentMappedUserId = this.normalizeSummaryString(summary?.mappedUserId);
    const nextMappedUserId = this.normalizeSummaryString(patch?.mapped_user_id);
    if (currentMappedUserId !== nextMappedUserId) return true;

    const currentSupersededBy = this.normalizeSummaryString(
      summary?.supersededByInvitationId
    );
    const nextSupersededBy = this.normalizeSummaryString(
      patch?.superseded_by_invitation_id
    );
    if (currentSupersededBy !== nextSupersededBy) return true;

    const currentResolutionReason = this.normalizeMappingResolutionReason(
      summary?.mappingResolutionReason
    );
    const nextResolutionReason = this.normalizeMappingResolutionReason(
      patch?.mapping_resolution_reason
    );
    return currentResolutionReason !== nextResolutionReason;
  }

  scheduleInvitationMappingReconciliation(coach) {
    if (!coach?.id) return;
    if (!this.isInvitationMappingHardeningEnabled()) return;
    if (this.mappingReconciliationInFlight) return;

    void this.runInvitationMappingReconciliationQueue(coach);
  }

  async runInvitationMappingReconciliationQueue(coach) {
    if (this.mappingReconciliationInFlight) return;

    this.mappingReconciliationInFlight = true;
    try {
      try {
        await refreshInvitationMappingHardeningFlag();
      } catch (error) {
        console.warn(
          "[invitation-mapping-hardening] flag_refresh_failed",
          error?.message ?? error
        );
      }

      if (!this.isInvitationMappingHardeningEnabled()) {
        this.mappingTouchedEmails.clear();
        this.mappingConflictEmails.clear();
        return;
      }

      while (this.mappingTouchedEmails.size > 0) {
        const normalizedEmails = Array.from(this.mappingTouchedEmails);
        this.mappingTouchedEmails.clear();

        for (const normalizedEmail of normalizedEmails) {
          await this.reconcileInvitationMappingForEmail(coach, normalizedEmail);
        }
      }
    } finally {
      this.mappingReconciliationInFlight = false;
      if (
        this.mappingTouchedEmails.size > 0 &&
        this.isInvitationMappingHardeningEnabled()
      ) {
        this.scheduleInvitationMappingReconciliation(coach);
      }
    }
  }

  async reconcileInvitationMappingForEmail(coach, normalizedEmail) {
    if (!normalizedEmail) return;
    if (!Array.isArray(coach?.invitationSummaries)) return;

    const activeUserIds = this.getActiveUserIdsForEmail(coach, normalizedEmail);
    if (activeUserIds.length > 1) {
      this.mappingConflictEmails.add(normalizedEmail);
      console.warn("[invitation-mapping-hardening] unresolved_conflict", {
        coachId: coach?.id ?? null,
        normalizedEmail,
        activeUserCount: activeUserIds.length,
      });
    } else {
      this.mappingConflictEmails.delete(normalizedEmail);
    }

    const auditPlan = buildInvitationMappingAuditPlan({
      invitationSummaries: coach.invitationSummaries,
      normalizedEmail,
      activeUserIds,
    });
    if (auditPlan.length === 0) return;

    for (const entry of auditPlan) {
      const invitationId = this.normalizeSummaryString(entry?.invitationId);
      if (!invitationId) continue;
      if (this.mappingAuditUpdatesInFlight.has(invitationId)) continue;

      const currentSummary = coach.invitationSummaries.find(
        (summary) => summary?.invitationId === invitationId
      );
      if (!this.shouldApplyInvitationMappingPatch(currentSummary, entry.patch)) {
        continue;
      }

      this.mappingAuditUpdatesInFlight.add(invitationId);
      try {
        await updateDoc(doc(db, "invitations", invitationId), {
          ...entry.patch,
          mapping_resolved_at: serverTimestamp(),
        });

        this.upsertInvitationSummary(coach, {
          invitationId,
          mappingStatus: entry.patch.mapping_status,
          mappedUserId: entry.patch.mapped_user_id,
          supersededByInvitationId: entry.patch.superseded_by_invitation_id,
          mappingResolutionReason: entry.patch.mapping_resolution_reason,
        });

      } catch (error) {
        const errorMessage = String(
          error?.message ?? "unknown invitation mapping reconcile error"
        );
        console.error("[invitation-mapping-hardening] reconcile_update_failed", {
          coachId: coach?.id ?? null,
          invitationId,
          normalizedEmail,
          errorMessage,
        });

        try {
          Sentry.withScope((scope) => {
            scope.setTag("component", "CoachFirestoreListener");
            scope.setTag("invitation_mapping_hardening_error", "true");
            scope.setExtra("coachId", coach?.id ?? null);
            scope.setExtra("invitationId", invitationId);
            scope.setExtra("normalizedEmail", normalizedEmail);
            scope.setExtra("errorMessage", errorMessage);
            Sentry.captureException(
              error instanceof Error ? error : new Error(errorMessage)
            );
          });
        } catch (_sentryError) {
          // no-op
        }
      } finally {
        this.mappingAuditUpdatesInFlight.delete(invitationId);
      }
    }
  }

  buildInvitationSummary({ invitationId, invitationData, invitationEmail }) {
    return {
      invitationId: String(invitationId ?? "").trim(),
      invitationEmail: String(invitationEmail ?? "").trim(),
      pendingUserId:
        typeof invitationData?.pending_user_id === "string"
          ? invitationData.pending_user_id.trim()
          : "",
      promotionStatus: this.normalizeInvitationPromotionStatus(
        invitationData?.promotion_status
      ),
      mappingStatus: this.normalizeInvitationMappingStatus(
        invitationData?.mapping_status
      ),
      mappedUserId:
        typeof invitationData?.mapped_user_id === "string"
          ? invitationData.mapped_user_id.trim()
          : "",
      supersededByInvitationId:
        typeof invitationData?.superseded_by_invitation_id === "string"
          ? invitationData.superseded_by_invitation_id.trim()
          : "",
      mappingResolutionReason: this.normalizeMappingResolutionReason(
        invitationData?.mapping_resolution_reason
      ),
      state: normalizeInvitationState(invitationData?.state),
      timestampMs: this.resolveInvitationCreatedAtMs(invitationData?.timestamp) ?? 0,
    };
  }

  upsertInvitationSummary(coach, summary) {
    const invitationId = String(summary?.invitationId ?? "").trim();
    if (!invitationId) return;

    if (!Array.isArray(coach.invitationSummaries)) {
      coach.invitationSummaries = [];
    }

    const summaryIndex = coach.invitationSummaries.findIndex(
      (entry) => entry?.invitationId === invitationId
    );

    if (summaryIndex === -1) {
      coach.invitationSummaries.push(summary);
      return;
    }

    coach.invitationSummaries[summaryIndex] = {
      ...coach.invitationSummaries[summaryIndex],
      ...summary,
    };
  }

  removeInvitationSummary(coach, invitationId) {
    const normalizedInvitationId = String(invitationId ?? "").trim();
    if (!normalizedInvitationId) return;
    if (!Array.isArray(coach.invitationSummaries)) return;

    coach.invitationSummaries = coach.invitationSummaries.filter(
      (entry) => entry?.invitationId !== normalizedInvitationId
    );
  }

  clearPendingInvitationHeadlineState(invitationId) {
    const normalizedInvitationId = this.normalizeSummaryString(invitationId);
    if (!normalizedInvitationId) return;

    this.pendingInvitationHeadlineInFlight.delete(normalizedInvitationId);
    this.pendingInvitationHeadlineHydratedPendingUsers.delete(
      normalizedInvitationId
    );
  }

  clearAllPendingInvitationHeadlineState() {
    this.pendingInvitationHeadlineInFlight.clear();
    this.pendingInvitationHeadlineHydratedPendingUsers.clear();
  }

  getPendingInvitationClient(coach, invitationId) {
    const normalizedInvitationId = this.normalizeSummaryString(invitationId);
    if (!normalizedInvitationId) return null;
    if (!Array.isArray(coach?.clients)) return null;

    return (
      coach.clients.find(
        (client) =>
          this.normalizeSummaryString(client?.invitationId) ===
          normalizedInvitationId
      ) ?? null
    );
  }

  findInvitationSummaryById(coach, invitationId) {
    const normalizedInvitationId = this.normalizeSummaryString(invitationId);
    if (!normalizedInvitationId) return null;
    if (!Array.isArray(coach?.invitationSummaries)) return null;

    return (
      coach.invitationSummaries.find(
        (summary) =>
          this.normalizeSummaryString(summary?.invitationId) ===
          normalizedInvitationId
      ) ?? null
    );
  }

  async resolveInvitationStateForClientSync({
    coach,
    invitationId,
    forceCloudRead = false,
  }) {
    const summary = this.findInvitationSummaryById(coach, invitationId);
    const summaryState = normalizeInvitationState(summary?.state);
    if (summaryState && !forceCloudRead) return summaryState;

    const normalizedInvitationId = this.normalizeSummaryString(invitationId);
    if (!normalizedInvitationId) return "";

    try {
      const invitationSnapshot = await getDoc(
        doc(db, "invitations", normalizedInvitationId)
      );
      if (!invitationSnapshot.exists()) {
        return summaryState;
      }

      const invitationData = invitationSnapshot.data() ?? {};
      const invitationEmail = resolveInvitationEmail(invitationData);
      const normalizedState = normalizeInvitationState(invitationData?.state);
      this.upsertInvitationSummary(
        coach,
        this.buildInvitationSummary({
          invitationId: normalizedInvitationId,
          invitationData,
          invitationEmail,
        })
      );

      return normalizedState || summaryState;
    } catch (error) {
      console.warn("[invitation-offline-sync] resolve_invitation_state_failed", {
        coachId: coach?.id ?? null,
        invitationId: normalizedInvitationId,
        error: error?.message ?? error,
      });
      return summaryState;
    }
  }

  async resolveMappedUserForAcceptedInvitation({ coach, normalizedEmail }) {
    if (!coach?.id || !normalizedEmail) {
      return {
        status: "deferred",
        reason: "invalid_lookup_input",
        userData: null,
        userId: "",
      };
    }

    const usersQuery = query(
      collection(db, "users"),
      where("coach_id", "==", coach.id),
      where("email", "==", normalizedEmail),
      limit(2)
    );

    const usersSnapshot = await getDocs(usersQuery);
    if (usersSnapshot.size === 1) {
      const userDoc = usersSnapshot.docs[0];
      const userData = userDoc.data() || {};
      const userId = this.normalizeSummaryString(userData?.user_id ?? userDoc.id);

      if (!userId) {
        return {
          status: "deferred",
          reason: "target_user_not_found",
          userData: null,
          userId: "",
        };
      }

      return {
        status: "ready",
        userData: {
          ...userData,
          user_id: userId,
        },
        userId,
      };
    }

    if (usersSnapshot.size > 1) {
      return {
        status: "deferred",
        reason: "multiple_users_for_email",
        userData: null,
        userId: "",
      };
    }

    const fallbackMatchedActiveClients = Array.isArray(coach?.activeClients)
      ? coach.activeClients.filter(
          (activeClient) => normalizeMappingEmail(activeClient?.email) === normalizedEmail
        )
      : [];
    if (fallbackMatchedActiveClients.length === 1) {
      const fallbackClient = fallbackMatchedActiveClients[0];
      const fallbackUserId = this.normalizeSummaryString(fallbackClient?.id);
      if (fallbackUserId) {
        return {
          status: "ready",
          userData: {
            ...fallbackClient.uploadToCloud?.(),
            user_id: fallbackUserId,
            email: fallbackClient?.email ?? normalizedEmail,
          },
          userId: fallbackUserId,
        };
      }
    }

    if (fallbackMatchedActiveClients.length > 1) {
      return {
        status: "deferred",
        reason: "multiple_users_for_email",
        userData: null,
        userId: "",
      };
    }

    return {
      status: "deferred",
      reason: "target_user_not_found",
      userData: null,
      userId: "",
    };
  }

  async reconcilePendingClientRoutingForSync({
    coach,
    client,
    forceCloudRead = false,
  }) {
    if (!coach || !client) {
      return { status: "ready", client };
    }

    if (!shouldUsePendingBlockPath(client)) {
      return { status: "ready", client };
    }

    const invitationId = this.normalizeSummaryString(client?.invitationId);
    if (!invitationId) {
      return { status: "ready", client };
    }

    const invitationState = await this.resolveInvitationStateForClientSync({
      coach,
      invitationId,
      forceCloudRead,
    });
    if (invitationState !== INVITATION_STATE.ACCEPTED) {
      return { status: "ready", client };
    }

    const normalizedInvitationEmail = normalizeMappingEmail(
      client?.invitationEmail ?? client?.email
    );
    if (!normalizedInvitationEmail) {
      console.info("[invitation-offline-sync] reconcile_result", {
        coachId: coach?.id ?? null,
        invitationId,
        pendingUserId: client?.pendingUserId ?? "",
        status: "deferred",
        reason: "invitation_email_missing",
      });
      return {
        status: "deferred",
        reason: "invitation_email_missing",
        client,
      };
    }

    try {
      const matchedUser = await this.resolveMappedUserForAcceptedInvitation({
        coach,
        normalizedEmail: normalizedInvitationEmail,
      });
      if (matchedUser?.status !== "ready" || !matchedUser?.userId) {
        console.info("[invitation-offline-sync] reconcile_result", {
          coachId: coach?.id ?? null,
          invitationId,
          pendingUserId: client?.pendingUserId ?? "",
          status: "deferred",
          reason: matchedUser?.reason ?? "target_user_not_found",
        });
        return {
          status: "deferred",
          reason: matchedUser?.reason ?? "target_user_not_found",
          client,
        };
      }

      const existingClientIds = new Set(
        (coach?.clients || []).map((entry) =>
          this.normalizeSummaryString(entry?.id).toLowerCase()
        )
      );
      const existingActiveClient = (coach?.clients || []).find(
        (entry) =>
          entry !== client &&
          this.normalizeSummaryString(entry?.id).toLowerCase() ===
            matchedUser.userId.toLowerCase()
      );

      const promotionResult = this.promotePendingClientToRealUser({
        coach,
        pendingClient: client,
        existingActiveClient,
        userData: matchedUser.userData ?? {},
        userId: matchedUser.userId,
        existingClientIds,
      });
      const promotedClient = promotionResult?.client ?? null;

      if (!promotedClient) {
        return { status: "ready", client };
      }

      const remapResult = promotionResult?.remapResult ?? {
        changed: false,
        promotedIdMap: {},
      };

      console.info("[invitation-offline-sync] reconcile_result", {
        coachId: coach?.id ?? null,
        invitationId,
        pendingUserId: client?.pendingUserId ?? "",
        status: "ready",
        promotedUserId: matchedUser.userId,
        remappedBlockIds: Object.keys(remapResult?.promotedIdMap ?? {}).length,
      });

      return {
        status: "ready",
        client: promotedClient,
      };
    } catch (error) {
      console.warn("[invitation-offline-sync] reconcile_failed", {
        coachId: coach?.id ?? null,
        invitationId,
        pendingUserId: client?.pendingUserId ?? "",
        error: error?.message ?? error,
      });
      return {
        status: "deferred",
        reason: "reconcile_error",
        client,
      };
    }
  }

  canHydratePendingInvitationHeadline(client) {
    if (!client || typeof client !== "object") return false;
    if (client?.status === "active-string") return false;
    if (client?.usePendingBlocksPath !== true) return false;

    const pendingUserId = this.normalizeSummaryString(client?.pendingUserId);
    return pendingUserId !== "";
  }

  schedulePendingInvitationHeadlineHydration({ coach, invitationId }) {
    const normalizedInvitationId = this.normalizeSummaryString(invitationId);
    if (!normalizedInvitationId || !coach?.id) return;

    const invitationClient = this.getPendingInvitationClient(
      coach,
      normalizedInvitationId
    );

    if (!this.canHydratePendingInvitationHeadline(invitationClient)) {
      this.clearPendingInvitationHeadlineState(normalizedInvitationId);
      return;
    }

    const pendingUserId = this.normalizeSummaryString(
      invitationClient?.pendingUserId
    );
    if (!pendingUserId) {
      this.clearPendingInvitationHeadlineState(normalizedInvitationId);
      return;
    }

    const lastHydratedPendingUserId =
      this.pendingInvitationHeadlineHydratedPendingUsers.get(
        normalizedInvitationId
      );
    if (lastHydratedPendingUserId === pendingUserId) {
      return;
    }

    if (this.pendingInvitationHeadlineInFlight.has(normalizedInvitationId)) {
      return;
    }

    const requestId = this.pendingInvitationHeadlineRequestSeq + 1;
    this.pendingInvitationHeadlineRequestSeq = requestId;
    this.pendingInvitationHeadlineInFlight.set(normalizedInvitationId, requestId);

    void this.hydratePendingInvitationHeadline({
      coach,
      invitationId: normalizedInvitationId,
      pendingUserId,
      requestId,
    }).finally(() => {
      const inFlightRequestId = this.pendingInvitationHeadlineInFlight.get(
        normalizedInvitationId
      );
      if (inFlightRequestId === requestId) {
        this.pendingInvitationHeadlineInFlight.delete(normalizedInvitationId);
      }
    });
  }

  async hydratePendingInvitationHeadline({
    coach,
    invitationId,
    pendingUserId,
    requestId,
  }) {
    const coachId = this.normalizeSummaryString(coach?.id);
    if (!coachId || !pendingUserId || !invitationId) return;

    try {
      const pendingBlocksQuery = query(
        collection(db, "coaches", coachId, "pending_users", pendingUserId, "blocks"),
        orderBy("last_update", "desc"),
        limit(2)
      );
      const blocksSnapshot = await getDocs(pendingBlocksQuery);
      const headline = resolvePendingInvitationBlockHeadline(
        blocksSnapshot.docs.map((snapshotDoc) => snapshotDoc.data())
      );

      runInAction(() => {
        if (this.pendingInvitationHeadlineInFlight.get(invitationId) !== requestId) {
          return;
        }

        const invitationClient = this.getPendingInvitationClient(coach, invitationId);
        if (!this.canHydratePendingInvitationHeadline(invitationClient)) {
          return;
        }

        const currentPendingUserId = this.normalizeSummaryString(
          invitationClient?.pendingUserId
        );
        if (currentPendingUserId !== pendingUserId) {
          return;
        }

        invitationClient.setProperty("blockCurrentName", headline.blockCurrentName);
        invitationClient.setProperty("blockCurrentWeek", headline.blockCurrentWeek);
        invitationClient.setProperty("blockWeeks", headline.blockWeeks);
        invitationClient.setProperty(
          "blockCurrentWeekPrepared",
          headline.blockCurrentWeekPrepared
        );

        this.pendingInvitationHeadlineHydratedPendingUsers.set(
          invitationId,
          pendingUserId
        );
      });
    } catch (error) {
      console.warn("[pending-invitation-headline] hydration_failed", {
        coachId,
        invitationId,
        pendingUserId,
        error: error?.message ?? error,
      });
    }
  }

  replaceFetchedClientId({ coach, previousId, nextId }) {
    if (!Array.isArray(coach?.fetchedClientIdsInMemory)) return;

    const normalizedPreviousId = String(previousId ?? "")
      .trim()
      .toLowerCase();
    const normalizedNextId = String(nextId ?? "")
      .trim()
      .toLowerCase();

    if (!normalizedPreviousId || !normalizedNextId) return;

    let didReplace = false;
    const dedupedIds = [];
    const seen = new Set();

    coach.fetchedClientIdsInMemory.forEach((rawId) => {
      const id = String(rawId ?? "").trim();
      if (!id) return;

      const normalizedId = id.toLowerCase();
      const resolvedId =
        normalizedId === normalizedPreviousId ? String(nextId) : id;
      const resolvedNormalizedId = resolvedId.toLowerCase();

      if (normalizedId === normalizedPreviousId) {
        didReplace = true;
      }

      if (seen.has(resolvedNormalizedId)) return;
      seen.add(resolvedNormalizedId);
      dedupedIds.push(resolvedId);
    });

    if (didReplace) {
      coach.fetchedClientIdsInMemory = dedupedIds;
    }
  }

  persistPendingStateForPromotedClient({ pendingClient, targetUserId, userData }) {
    const normalizedTargetUserId = this.normalizeSummaryString(targetUserId);
    if (!normalizedTargetUserId) {
      return;
    }

    const statePatch = {};

    if (userData?.is_checked === undefined) {
      statePatch.is_checked = pendingClient?.isChecked === true;
    }

    if (userData?.week_workouts === undefined) {
      statePatch.week_workouts = normalizePendingWorkspaceWeekWorkouts(
        pendingClient?.weekWorkouts
      );
    }

    if (Object.keys(statePatch).length === 0) {
      return;
    }

    void setDoc(doc(db, "users", normalizedTargetUserId), statePatch, {
      merge: true,
    }).catch((error) => {
      console.warn("[invitation-mapping] promoted state fallback failed", {
        userId: normalizedTargetUserId,
        error: error?.message ?? error,
      });
    });
  }

  promotePendingClientToRealUser({
    coach,
    pendingClient,
    existingActiveClient,
    userData,
    userId,
    existingClientIds,
  }) {
    if (!pendingClient) {
      return {
        client: null,
        remapResult: {
          changed: false,
          promotedIdMap: {},
        },
      };
    }

    const nextUserId = String(userId ?? "").trim();
    if (!nextUserId) {
      return {
        client: null,
        remapResult: {
          changed: false,
          promotedIdMap: {},
        },
      };
    }

    const normalizedNextUserId = nextUserId.toLowerCase();
    const previousClientId = String(pendingClient.id ?? "").trim();
    const normalizedPreviousClientId = previousClientId.toLowerCase();
    const wasPendingWorkspaceRouted = pendingClient?.usePendingBlocksPath === true;
    const pendingInvitationId = this.normalizeSummaryString(
      pendingClient?.invitationId
    );

    if (pendingInvitationId) {
      this.clearPendingInvitationHeadlineState(pendingInvitationId);
    }

    const isPendingClientTracked = Array.isArray(coach?.clients)
      ? coach.clients.includes(pendingClient)
      : false;

    if (
      isPendingClientTracked &&
      existingActiveClient &&
      existingActiveClient !== pendingClient
    ) {
      coach.clients = coach.clients.filter(
        (client) => client !== existingActiveClient
      );
    }

    if (
      normalizedPreviousClientId &&
      normalizedPreviousClientId !== normalizedNextUserId
    ) {
      existingClientIds.delete(normalizedPreviousClientId);
    }

    pendingClient.setProperty("id", nextUserId);
    pendingClient.setProperty("status", "active-string");
    pendingClient.setProperty("usePendingBlocksPath", false);

    if (typeof userData?.email === "string" && userData.email.trim() !== "") {
      pendingClient.setProperty("email", userData.email);
      pendingClient.setProperty("invitationEmail", userData.email);
    }

    if (
      typeof userData?.user_name === "string" &&
      userData.user_name.trim() !== ""
    ) {
      pendingClient.setProperty("name", userData.user_name);
    }

    if (typeof userData?.user_surname === "string") {
      pendingClient.setProperty("surname", userData.user_surname);
    }

    if (
      typeof userData?.name_for_coach === "string" &&
      userData.name_for_coach.trim() !== ""
    ) {
      pendingClient.setProperty("nameForCoach", userData.name_for_coach);
    } else if (
      typeof userData?.user_name === "string" &&
      userData.user_name.trim() !== ""
    ) {
      pendingClient.setProperty("nameForCoach", userData.user_name);
    }

    if (
      typeof userData?.surname_for_coach === "string" &&
      userData.surname_for_coach.trim() !== ""
    ) {
      pendingClient.setProperty("surnameForCoach", userData.surname_for_coach);
    } else if (typeof userData?.user_surname === "string") {
      pendingClient.setProperty("surnameForCoach", userData.user_surname);
    }

    if (userData?.bodyweight_unit !== undefined) {
      pendingClient.setProperty("bodyweightUnit", userData.bodyweight_unit);
    }

    if (userData?.exercise_unit !== undefined) {
      pendingClient.setProperty("exerciseUnit", userData.exercise_unit);
    }

    if (userData?.language !== undefined) {
      pendingClient.setProperty("language", userData.language);
    }

    if (userData?.notes !== undefined) {
      pendingClient.setProperty("notes", userData.notes ?? "");
    }

    if (userData?.phone !== undefined) {
      pendingClient.setProperty("phone", userData.phone ?? "");
    }

    if (userData?.gender !== undefined) {
      pendingClient.setProperty("gender", userData.gender);
    }

    pendingClient.updateClient(userData);
    pendingClient.clientNutritionPlanStore?.rebindClientWorkspace?.();
    if (wasPendingWorkspaceRouted) {
      this.persistPendingStateForPromotedClient({
        pendingClient,
        targetUserId: nextUserId,
        userData,
      });
    }

    if (
      normalizedPreviousClientId &&
      String(coach.selectedClientId ?? "").trim().toLowerCase() ===
        normalizedPreviousClientId
    ) {
      coach.selectedClientId = nextUserId;
    }

    if (
      normalizedPreviousClientId &&
      String(coach.selectedChatId ?? "").trim().toLowerCase() ===
        normalizedPreviousClientId
    ) {
      coach.selectedChatId = nextUserId;
    }

    this.replaceFetchedClientId({
      coach,
      previousId: previousClientId,
      nextId: nextUserId,
    });

    existingClientIds.add(normalizedNextUserId);
    const remapResult = wasPendingWorkspaceRouted
      ? remapClientPendingBlockIdsToPromotedIds({
          client: pendingClient,
          pendingUserId: this.normalizeSummaryString(pendingClient?.pendingUserId),
        })
      : {
          changed: false,
          promotedIdMap: {},
        };

    return {
      client: pendingClient,
      remapResult,
    };
  }

  shouldRehydratePromotedClient(coach, client) {
    const normalizedClientId = String(client?.id ?? "")
      .trim()
      .toLowerCase();
    if (!normalizedClientId) return false;

    if (
      String(coach?.selectedClientId ?? "").trim().toLowerCase() ===
      normalizedClientId
    ) {
      return true;
    }

    if (!Array.isArray(coach?.fetchedClientIdsInMemory)) {
      return false;
    }

    return coach.fetchedClientIdsInMemory.some(
      (id) => String(id ?? "").trim().toLowerCase() === normalizedClientId
    );
  }

  /* CLIENTS LISTENER */
  async setupClientsListener(coach) {
    return new Promise((resolve, reject) => {
      if (this.clientsListener) {
        console.log("Removing old clients listener...");
        this.removeClientsListener();
      }

      if (!coach?.id) {
        console.warn("No coach ID provided, skipping clients listener setup.");
        return reject(new Error("No coach ID provided"));
      }

      console.log("Setting up clients listener for coach:", coach.id);

      let initialRun = true;

      const clientsQuery = query(
        collection(db, "users"),
        where("coach_id", "==", coach.id)
      );

      this.clientsListener = onSnapshot(
        clientsQuery,
        (querySnapshot) => {
          const clientsToRehydrate = new Set();
          const snapshotConflictEmails = new Set();
          const snapshotEmailCounts = new Map();

          if (this.isInvitationMappingHardeningEnabled()) {
            querySnapshot.docs.forEach((snapshotDoc) => {
              const normalizedEmail = normalizeMappingEmail(
                snapshotDoc.data()?.email
              );
              if (!normalizedEmail) return;

              snapshotEmailCounts.set(
                normalizedEmail,
                (snapshotEmailCounts.get(normalizedEmail) ?? 0) + 1
              );
            });

            snapshotEmailCounts.forEach((count, normalizedEmail) => {
              if (count > 1) {
                snapshotConflictEmails.add(normalizedEmail);
              }
            });
            this.mappingConflictEmails = snapshotConflictEmails;
          }

          try {
            runInAction(() => {
              const existingClientIds = new Set(
                coach.clients.map((client) =>
                  client.id.toString().toLowerCase()
                )
              );

              querySnapshot.docChanges().forEach((change) => {
                const rawClientData = change.doc.data() || {};
                const clientData = {
                  ...rawClientData,
                  // Always use the users/{docId} path as the canonical id. A stored
                  // `user_id` field may be lowercased and must not override Firestore casing.
                  user_id: change.doc.id,
                };
                const clientId = String(clientData.user_id ?? "")
                  .trim()
                  .toLowerCase();
                const normalizedClientEmail = normalizeMappingEmail(
                  clientData.email
                );
                this.recordMappingTouchedEmail(normalizedClientEmail);
                const shouldSkipPromotionForConflict =
                  this.shouldSkipPendingPromotionForConflict({
                    coach,
                    normalizedEmail: normalizedClientEmail,
                  });

                const existingActiveClient = coach.clients.find(
                  (client) =>
                    String(client.id ?? "").trim().toLowerCase() === clientId
                );

                const canonicalPendingClient =
                  selectCanonicalPendingClientForEmail(
                    coach.clients,
                    normalizedClientEmail
                  );

                switch (change.type) {
                  case "added":
                    if (
                      canonicalPendingClient &&
                      !shouldSkipPromotionForConflict
                    ) {
                      const promotionResult = this.promotePendingClientToRealUser(
                        {
                          coach,
                          pendingClient: canonicalPendingClient,
                          existingActiveClient,
                          userData: clientData,
                          userId: clientData.user_id,
                          existingClientIds,
                        }
                      );
                      const promotedClient = promotionResult?.client ?? null;
                      if (
                        promotedClient &&
                        this.shouldRehydratePromotedClient(coach, promotedClient)
                      ) {
                        clientsToRehydrate.add(promotedClient);
                      }
                    } else if (!existingClientIds.has(clientId)) {
                      coach.fetchClient(clientData);
                      existingClientIds.add(clientId);
                    }
                    break;

                  case "modified":
                    const foundClient = coach.clients.find(
                      (client) => client.id.toLowerCase() === clientId
                    );
                    const promotionResult =
                      canonicalPendingClient && !shouldSkipPromotionForConflict
                      ? this.promotePendingClientToRealUser({
                          coach,
                          pendingClient: canonicalPendingClient,
                          existingActiveClient,
                          userData: clientData,
                          userId: clientData.user_id,
                          existingClientIds,
                        })
                      : null;
                    const promotedClient = promotionResult?.client ?? null;
                    if (
                      !promotedClient &&
                      !existingActiveClient &&
                      !existingClientIds.has(clientId)
                    ) {
                      coach.fetchClient(clientData);
                      existingClientIds.add(clientId);
                    }
                    if (
                      promotedClient &&
                      this.shouldRehydratePromotedClient(coach, promotedClient)
                    ) {
                      clientsToRehydrate.add(promotedClient);
                    }

                    const updatedClient =
                      promotedClient ||
                      foundClient ||
                      coach.clients.find(
                        (client) =>
                          client.id.toString().toLowerCase() === clientId
                      );
                    if (updatedClient) {
                      updatedClient.updateClient(clientData);
                    }

                    break;
                  case "removed":
                    console.log("user removed");
                    coach.clients = coach.clients.filter(
                      (client) =>
                        client.id.toString().toLowerCase() !== clientId
                    );
                    coach.fetchedClientIdsInMemory =
                      coach.fetchedClientIdsInMemory.filter(
                        (id) =>
                          String(id ?? "").trim().toLowerCase() !== clientId
                      );
                    existingClientIds.delete(clientId);
                    break;
                }
              });
            });
            this.scheduleInvitationMappingReconciliation(coach);

            if (clientsToRehydrate.size > 0) {
              clientsToRehydrate.forEach((client) => {
                client.firestoreListener?.removeBlocksListener?.();
                client.syncStatusManager?.setOperationStatus?.(
                  "listenerBlocks",
                  null
                );
                client
                  .fetchInit()
                  .catch((error) =>
                    console.error("Failed to rehydrate promoted client:", error)
                  );
              });
            }

            if (initialRun) {
              initialRun = false;
              resolve();
            }
          } catch (err) {
            console.error("Error processing clients listener:", err);
            if (initialRun) {
              initialRun = false;
              reject(err);
            }
          }
        },
        (error) => {
          console.error("Error with clients listener:", error);
          // crashlytics().recordError(error);
          if (error.code === "permission-denied") {
            console.log("Permission error, stopping clients listener.");
            this.removeClientsListener();
          }
          reject(error);
        }
      );
    });
  }

  removeClientsListener() {
    if (this.clientsListener) {
      console.log("Removing clients listener...");
      this.clientsListener();
      this.clientsListener = null;
    } else {
      console.log("No active clients listener to remove.");
    }
  }

  async backfillLegacyInvitationStates(coach) {
    const coachId = this.normalizeSummaryString(coach?.id);
    if (!coachId) return;

    try {
      const invitationsSnapshot = await getDocs(
        query(collection(db, "invitations"), where("coach_id", "==", coachId))
      );

      if (invitationsSnapshot.empty) {
        return;
      }

      const docsToBackfill = invitationsSnapshot.docs.filter((snapshotDoc) => {
        const invitationData = snapshotDoc.data?.() ?? {};
        return normalizeInvitationState(invitationData?.state) === "";
      });

      if (docsToBackfill.length === 0) {
        return;
      }

      const CHUNK_SIZE = 350;
      let updatedCount = 0;
      let failedCount = 0;

      for (let index = 0; index < docsToBackfill.length; index += CHUNK_SIZE) {
        const chunk = docsToBackfill.slice(index, index + CHUNK_SIZE);
        const batch = writeBatch(db);

        chunk.forEach((snapshotDoc) => {
          batch.update(snapshotDoc.ref, {
            state: INVITATION_STATE.PENDING,
          });
        });

        try {
          await batch.commit();
          updatedCount += chunk.length;
        } catch (error) {
          failedCount += chunk.length;
          console.warn("[invitations] legacy state backfill chunk failed", {
            coachId,
            chunkSize: chunk.length,
            error: error?.message ?? error,
          });
        }
      }

      console.info("[invitations] legacy state backfill completed", {
        coachId,
        updatedCount,
        failedCount,
      });
    } catch (error) {
      console.warn("[invitations] legacy state backfill failed", {
        coachId,
        error: error?.message ?? error,
      });
    }
  }

  /* INVITATIONS LISTENER */
  setupInvitationsListener(coach) {
    return new Promise((resolve, reject) => {
      if (this.invitationsListener) {
        console.log("Removing old invitations listener...");
        this.removeInvitationsListener();
      }

      console.log("Setting up invitations listener for email:", coach.email);
      coach.invitationSummaries = [];
      this.mappingTouchedEmails.clear();
      this.mappingConflictEmails.clear();
      this.clearAllPendingInvitationHeadlineState();
      void refreshInvitationMappingHardeningFlag({ force: true }).catch((error) =>
        console.warn(
          "[invitation-mapping-hardening] initial_flag_refresh_failed",
          error?.message ?? error
        )
      );

      const startInvitationsSnapshotListener = () => {
        let initialRun = true;
        const invitationsQuery = query(
          collection(db, "invitations"),
          where("coach_id", "==", coach.id),
          where("state", "in", INVITATION_ACTIONABLE_STATES)
        );

        this.invitationsListener = onSnapshot(
          invitationsQuery,
          (querySnapshot) => {
            let shouldRefreshPendingWorkspaceSummaries = false;
            try {
              runInAction(() => {
                const existingInvitationIds = new Set(
                  coach.clients.map((client) => client.invitationId)
                );

                querySnapshot.docChanges().forEach((change) => {
                  const dataInvitation = change.doc.data();
                  const invitationId = change.doc.id;
                  const invitationState = normalizeInvitationState(
                    dataInvitation?.state
                  );
                  const invitationEmail = resolveInvitationEmail(dataInvitation);
                  const normalizedInvitationEmail =
                    normalizeMappingEmail(invitationEmail);
                  this.recordMappingTouchedEmail(normalizedInvitationEmail);
                  const pendingUserId =
                    typeof dataInvitation?.pending_user_id === "string"
                      ? dataInvitation.pending_user_id.trim()
                      : "";
                  const canRoutePendingBlocks = pendingUserId !== "";
                  const invitationCreatedAtMs = this.resolveInvitationCreatedAtMs(
                    dataInvitation?.timestamp
                  );
                  const invitationSummary = this.buildInvitationSummary({
                    invitationId,
                    invitationData: dataInvitation,
                    invitationEmail,
                  });
                  const isAcceptedInvitation =
                    invitationState === INVITATION_STATE.ACCEPTED;

                  switch (change.type) {
                    case "added":
                      this.upsertInvitationSummary(coach, invitationSummary);

                      if (isAcceptedInvitation) {
                        this.clearPendingInvitationHeadlineState(invitationId);
                        const pendingClient = this.getPendingInvitationClient(
                          coach,
                          invitationId
                        );
                        if (
                          shouldRetainAcceptedPendingClientForDeferredSync(
                            pendingClient
                          )
                        ) {
                          console.info(
                            "[invitation-offline-sync] retaining_accepted_pending_client",
                            {
                              coachId: coach?.id ?? null,
                              invitationId,
                              pendingUserId: pendingClient?.pendingUserId ?? "",
                            }
                          );
                          break;
                        }

                        coach.clients = coach.clients.filter(
                          (entry) =>
                            !(
                              entry.invitationId === invitationId &&
                              entry.status !== "active-string"
                            )
                        );
                        break;
                      }

                      if (!existingInvitationIds.has(invitationId)) {
                        if (
                          shouldSkipPendingClientCreation({
                            normalizedInvitationEmail,
                            activeClients: coach.activeClients,
                          })
                        ) {
                          existingInvitationIds.add(invitationId);
                          break;
                        }

                        console.log("New invitation", invitationId);

                        // Determine status based on emailStatus
                        const invitationStatus =
                          dataInvitation.emailStatus === "error"
                            ? "error-string"
                            : "pending-string";

                        const newClient = new Client(
                          undefined,
                          invitationEmail,
                          invitationEmail,
                          "",
                          invitationEmail,
                          "",
                          undefined,
                          undefined,
                          undefined,
                          undefined,
                          undefined,
                          undefined,
                          undefined,
                          undefined,
                          undefined,
                          coach,
                          undefined,
                          undefined,
                          undefined,
                          invitationStatus,
                          invitationId
                        );
                        newClient.setProperty("pendingUserId", pendingUserId);
                        newClient.setProperty(
                          "usePendingBlocksPath",
                          canRoutePendingBlocks
                        );
                        newClient.setProperty("invitationEmail", invitationEmail);
                        newClient.setProperty(
                          "invitationCreatedAtMs",
                          invitationCreatedAtMs
                        );
                        coach.applyPendingWorkspaceStateToClient?.(newClient, {
                          applyDefaultsWhenMissing: true,
                        });
                        coach.clients.push(newClient);
                        existingInvitationIds.add(invitationId);
                      }
                      this.schedulePendingInvitationHeadlineHydration({
                        coach,
                        invitationId,
                      });
                      break;

                    case "modified":
                      this.upsertInvitationSummary(coach, invitationSummary);
                      const clientToUpdate = coach.clients.find(
                        (client) => client.invitationId === invitationId
                      );

                      if (isAcceptedInvitation) {
                        this.clearPendingInvitationHeadlineState(invitationId);
                        if (
                          shouldRetainAcceptedPendingClientForDeferredSync(
                            clientToUpdate
                          )
                        ) {
                          console.info(
                            "[invitation-offline-sync] retaining_accepted_pending_client",
                            {
                              coachId: coach?.id ?? null,
                              invitationId,
                              pendingUserId: clientToUpdate?.pendingUserId ?? "",
                            }
                          );
                          break;
                        }

                        if (
                          clientToUpdate &&
                          clientToUpdate.status !== "active-string"
                        ) {
                          coach.clients = coach.clients.filter(
                            (entry) =>
                              !(
                                entry.invitationId === invitationId &&
                                entry.status !== "active-string"
                              )
                          );
                        }
                        break;
                      }

                      if (clientToUpdate) {
                        if (clientToUpdate.status !== "active-string") {
                          clientToUpdate.setProperty("email", invitationEmail);
                          clientToUpdate.setProperty("name", invitationEmail);
                          clientToUpdate.setProperty(
                            "nameForCoach",
                            invitationEmail
                          );
                          clientToUpdate.setProperty(
                            "pendingUserId",
                            pendingUserId
                          );
                          clientToUpdate.setProperty(
                            "usePendingBlocksPath",
                            canRoutePendingBlocks
                          );
                        }

                        clientToUpdate.setProperty(
                          "invitationEmail",
                          invitationEmail
                        );
                        clientToUpdate.setProperty(
                          "invitationCreatedAtMs",
                          invitationCreatedAtMs
                        );
                        coach.applyPendingWorkspaceStateToClient?.(
                          clientToUpdate,
                          {
                            applyDefaultsWhenMissing: true,
                          }
                        );
                      }

                      // Detect if emailStatus changed to "error"
                      if (dataInvitation.emailStatus === "error") {
                        console.log(
                          "Invitation email failed for:",
                          invitationEmail
                        );

                        // Update the client status to "error-string"
                        if (
                          clientToUpdate &&
                          clientToUpdate.status !== "active-string" &&
                          clientToUpdate.status !== "error-string"
                        ) {
                          clientToUpdate.setProperty("status", "error-string");

                          if (
                            coach.showToast &&
                            typeof coach.showToast === "function"
                          ) {
                            coach.showToast("invitationEmailError-string", {
                              type: "error",
                              description:
                                "invitationEmailErrorDescription-string",
                              variables: {
                                clientEmail: invitationEmail,
                              },
                            });
                          }
                        }
                      } else if (
                        clientToUpdate &&
                        clientToUpdate.status !== "active-string" &&
                        clientToUpdate.status !== "pending-string"
                      ) {
                        clientToUpdate.setProperty("status", "pending-string");
                      }

                      this.schedulePendingInvitationHeadlineHydration({
                        coach,
                        invitationId,
                      });
                      break;

                    case "removed":
                      this.clearPendingInvitationHeadlineState(invitationId);
                      this.removeInvitationSummary(coach, invitationId);
                      if (pendingUserId) {
                        shouldRefreshPendingWorkspaceSummaries = true;
                      }
                      console.log("Removed invitation", invitationId);
                      const clientIndex = coach.clients.findIndex(
                        (client) =>
                          client.invitationId === invitationId &&
                          client.status !== "active-string"
                      );

                      if (clientIndex !== -1) {
                        coach.clients.splice(clientIndex, 1);
                        existingInvitationIds.delete(invitationId);
                        console.log(
                          `Client with ID ${invitationId} removed from coach.clients.`
                        );
                      }
                      break;
                  }
                });
              });
              this.scheduleInvitationMappingReconciliation(coach);
              if (
                shouldRefreshPendingWorkspaceSummaries &&
                typeof coach.refreshPendingWorkspaceSummariesFromCloud ===
                  "function"
              ) {
                void coach
                  .refreshPendingWorkspaceSummariesFromCloud({
                    reason: "invitation_removed_from_actionable_states",
                  })
                  .catch((error) => {
                    console.warn(
                      "[pending-workspace] refresh_after_invitation_removed_failed",
                      {
                        coachId: coach?.id,
                        error: error?.message ?? error,
                      }
                    );
                  });
              }

              if (initialRun) {
                initialRun = false;
                resolve();
              }
            } catch (err) {
              console.error("Error processing invitations listener:", err);
              if (initialRun) {
                initialRun = false;
                reject(err);
              }
            }
          },
          (error) => {
            console.error("Error with invitations listener:", error);
            if (error.code === "permission-denied") {
              console.log("Permission error, stopping invitations listener.");
              this.removeInvitationsListener();
            }
            reject(error);
          }
        );
      };

      void this.backfillLegacyInvitationStates(coach).finally(() => {
        startInvitationsSnapshotListener();
      });
    });
  }

  removeInvitationsListener() {
    if (this.invitationsListener) {
      console.log("Removing invitations listener...");
      this.invitationsListener();
      this.invitationsListener = null;
      this.mappingTouchedEmails.clear();
      this.mappingConflictEmails.clear();
      this.clearAllPendingInvitationHeadlineState();
    } else {
      console.log("No active invitations listener to remove.");
    }
  }

  /* CHATS LISTENER */
  async setupChatsListener(coach) {
    return new Promise((resolve, reject) => {
      if (this.chatsListener) {
        console.log("Removing old chats listener...");
        this.removeChatsListener();
      }

      if (!coach?.id) {
        console.warn("No coach ID provided, skipping chats listener setup.");
        return reject(new Error("No coach ID provided"));
      }

      console.log("Setting up chats listener for coach:", coach.id);

      let initialRun = true;

      const chatsQuery = query(collection(db, "coaches", coach.id, "chats"));

      this.chatsListener = onSnapshot(
        chatsQuery,
        { includeMetadataChanges: false },
        (querySnapshot) => {
          try {
            querySnapshot.docChanges().forEach((change) => {
              const chatData = change.doc.data();

              const client = coach.clients.find(
                (client) =>
                  client.id.toString().toLowerCase() ===
                  change.doc?.id.toLowerCase()
              );

              if (
                (change.type === "added" || change.type === "modified") &&
                client
              ) {
                // console.log("new messages for client", change.doc.id);

                if (client.firestoreListener.chatListener === null) {
                  client.fetchChatLastMessages(chatData);
                }

                if (
                  client.firestoreListener.chatListener != null &&
                  coach.showingChatList
                ) {
                  const updateData = {};
                  if (
                    typeof chatData.unread_count_for_coach === "number" &&
                    chatData.unread_count_for_coach > 0
                  ) {
                    updateData.unread_count_for_coach = 0;
                  }

                  if (
                    Array.isArray(chatData.last_messages_from_client) &&
                    chatData.last_messages_from_client.length > 5
                  ) {
                    console.log(
                      "chatData.last_messages_from_client",
                      chatData.last_messages_from_client
                    );
                    const sortedMessages = [
                      ...chatData.last_messages_from_client,
                    ].sort((a, b) => a.timestamp - b.timestamp);
                    const messagesToRemove = sortedMessages.slice(
                      0,
                      sortedMessages.length - 5
                    );
                    updateData.last_messages_from_client = arrayRemove(
                      ...messagesToRemove
                    );
                    console.log(
                      "Removed messages from last_messages_from_client:",
                      messagesToRemove
                    );
                  }

                  if (Object.keys(updateData).length > 0) {
                    updateDoc(change.doc.ref, updateData)
                      .then(() => {
                        console.log("Firebase update successful");
                      })
                      .catch((err) => {
                        console.error("Firebase update failed:", err);
                      });
                  }
                } else {
                  client.setProperty(
                    "unreadCount",
                    chatData.unread_count_for_coach
                  );
                }
                if (typeof chatData.unread_mark === "boolean") {
                  client.setProperty("unreadMark", chatData.unread_mark);
                } else {
                  client.setProperty("unreadMark", false);
                }
              } else if (change.type === "removed") {
                console.log("chat removed");
                if (client) {
                  client.messages = [];
                }
              }
            });

            if (initialRun) {
              initialRun = false;
              resolve();
            }
          } catch (err) {
            console.error("Error processing chats listener:", err);
            if (initialRun) {
              initialRun = false;
              reject(err);
            }
          }
        },
        (error) => {
          console.error("Error with chats listener:", error);
          // crashlytics().recordError(error);
          if (error.code === "permission-denied") {
            console.log("Permission error, stopping chats listener.");
            this.removeChatsListener();
          }
          reject(error);
        }
      );
    });
  }

  removeChatsListener() {
    if (this.chatsListener) {
      console.log("Removing chats listener...");
      this.chatsListener();
      this.chatsListener = null;
    } else {
      console.log("No active chats listener to remove.");
    }
  }
}

export default CoachFirestoreListener;
